import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { Reorder, useDragControls } from 'motion/react';
import { useTags, type IdeaTag } from '../tags/store';
import './ideaTags.css';
import { NavSection } from '../nav/NavSection';

/**
 * The "Collections" sidebar section — the user's pinned tags, rendered as
 * folder-like nav rows. A Collection is just a pinned tag: clicking it opens the
 * Your Ideas list filtered to that tag. Rows reorder by dragging the grip
 * (motion Reorder), and unpin via right-click. Renders nothing when the account
 * has no pins, so a tagless rail never shows an empty section.
 *
 * Reused sidebar anatomy (`sidebar__item`, `sidebar__item-num`, `is-active`);
 * the only tag-lineage tell is the mono `#` in the icon slot.
 */
export function CollectionsNav({ onNavigate }: { onNavigate: () => void }) {
  const ensureLoaded = useTags((s) => s.ensureLoaded);
  const collectionTagIds = useTags((s) => s.collectionTagIds);
  const tagsById = useTags((s) => s.tagsById);
  const reorderCollections = useTags((s) => s.reorderCollections);
  const unpinCollection = useTags((s) => s.unpinCollection);

  const [location, navigate] = useLocation();
  const search = useSearch();
  const activeTag = new URLSearchParams(search).get('tag');

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  // Local drag order, kept in sync with the store except mid-drag.
  const [order, setOrder] = useState<string[]>(collectionTagIds);
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setOrder(collectionTagIds);
  }, [collectionTagIds]);

  // Right-click / long-press context menu (unpin).
  const [menu, setMenu] = useState<{ tagId: string; x: number; y: number } | null>(null);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  if (collectionTagIds.length === 0) return null;

  const persist = () => {
    dragging.current = false;
    reorderCollections(order).catch(() => {});
  };

  return (
    <NavSection
      id="collections"
      label="Collections"
      hint="Tags you've pinned. Click one to see just its ideas."
      count={collectionTagIds.length}
      containsActive={location === '/ideas' && !!activeTag && collectionTagIds.includes(activeTag)}
    >
      <Reorder.Group axis="y" values={order} onReorder={setOrder} as="div">
        {order.map((tagId) => {
          const tag = tagsById.get(tagId);
          if (!tag) return null;
          const active = location === '/ideas' && activeTag === tagId;
          return (
            <CollectionRow
              key={tagId}
              tagId={tagId}
              tag={tag}
              active={active}
              onOpen={() => {
                navigate(`/ideas?tag=${encodeURIComponent(tagId)}`);
                onNavigate();
              }}
              onDragStart={() => (dragging.current = true)}
              onDragEnd={persist}
              onContextMenu={(x, y) => setMenu({ tagId, x, y })}
            />
          );
        })}
      </Reorder.Group>

      {menu && (
        <div className="coll-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="coll-menu__item"
            onClick={() => {
              unpinCollection(menu.tagId).catch(() => {});
              setMenu(null);
            }}
          >
            <i />
            Remove from sidebar
          </button>
        </div>
      )}
    </NavSection>
  );
}

function CollectionRow({
  tagId,
  tag,
  active,
  onOpen,
  onDragStart,
  onDragEnd,
  onContextMenu,
}: {
  tagId: string;
  tag: IdeaTag;
  active: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onContextMenu: (x: number, y: number) => void;
}) {
  const controls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);

  return (
    <Reorder.Item
      value={tagId}
      as="div"
      dragListener={false}
      dragControls={controls}
      dragElastic={0.06}
      whileDrag={{ scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 550, damping: 35 }}
      onDragStart={() => {
        setIsDragging(true);
        onDragStart();
      }}
      onDragEnd={() => {
        setIsDragging(false);
        onDragEnd();
      }}
      className={`sidebar__item coll-item${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
      onClick={onOpen}
      onContextMenu={(e: React.MouseEvent) => {
        e.preventDefault();
        onContextMenu(e.clientX, e.clientY);
      }}
      style={{ cursor: 'pointer' }}
    >
      <span className="sidebar__item-num coll-hash" aria-hidden="true">#</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {tag.name}
      </span>
      <span className="coll-right">
        <span className="coll-cnt">{tag.ideaCount}</span>
        <span
          className="coll-grip"
          aria-label="Drag to reorder"
          onPointerDown={(e) => {
            e.stopPropagation();
            controls.start(e);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <i />
        </span>
      </span>
    </Reorder.Item>
  );
}
